import { useCallback, useState } from 'react'

import { RequestLog } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { CopyButton } from '#components/CopyButton.js'
import { CopyServiceRequestButton } from '#components/CopyServiceRequestButton.js'
import {
  RequestLogFilters,
  useRequestLogFilters,
} from '#components/RequestLogFilters.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { GetTrackingDetails } from './GetTrackingDetails'
import { GetTrackingsList } from './GetTrackingsList'

const GetTrackingsView = () => {
  const [selectedTracking, setSelectedTracking] = useState<RequestLog | null>(
    null,
  )
  const { filters, localFilters, setLocalFilters } = useRequestLogFilters()

  const {
    items: trackings,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<RequestLog>('request-logs', {
    type: 'track',
    ...filters,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleTrackingSelect = useCallback((tracking: RequestLog) => {
    setSelectedTracking(tracking)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedTracking(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Tracking Lookup Details</span>
      {selectedTracking && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?requestId=${selectedTracking.requestId}`}
          />
          {selectedTracking.tenantRequest?.content && (
            <CopyServiceRequestButton log={selectedTracking} />
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Tracking Lookups"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No tracking lookups found"
        filters={
          <RequestLogFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            errorTypes={[
              'authentication',
              'validation',
              'internal',
              'external',
            ]}
            statuses={[
              'pending',
              'enroute-to-pickup',
              'in-transit',
              'out-for-delivery',
              'delivered',
              'not-found',
              'error',
            ]}
            showTrackingNumber
          />
        }
        listContent={
          trackings.length > 0 ? (
            <GetTrackingsList
              trackings={trackings}
              selectedId={selectedTracking?._id}
              onSelect={handleTrackingSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedTracking}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <GetTrackingDetails tracking={selectedTracking} />
      </Slider>
    </>
  )
}

export { GetTrackingsView }
